import { TIdentityDocumentType } from '@verifime/utils';
import { TProblemDocumentType } from '../CustomerConfidence/useCustomerConfidence';

/**
 * What the confidence response's document types are called in the layer that draws the mark.
 *
 * **Two vocabularies, reconciled here because here is where the contract legitimately lives.** The
 * response spells its documents with the person API's `IdentityDocumentType`, which is a contract
 * and can move. The KYC table matches its rows against `@verifime/utils`' `TIdentityDocumentType`,
 * which is the frontend's own and cannot: `@verifime/components` must not depend on the API even at
 * the type level, because a component that matched on the generated enum would silently stop
 * marking anything the day that enum was respelled, in the layer furthest from the change.
 *
 * **Written out rather than cast, even though the two spellings agree today.** A `Record` keyed on
 * the generated type is what turns an upstream rename or addition of a document type into a compile
 * error in this file, which knows about the API, instead of a mark that quietly stops appearing in
 * a file that does not. The day the two do diverge, nothing about this file's callers has to
 * change.
 */
const DOCUMENT_NAME: Record<TProblemDocumentType, TIdentityDocumentType> = {
  Passport: 'Passport',
  ImmiCard: 'ImmiCard',
  DriverLicence: 'DriverLicence',
  MedicareCard: 'MedicareCard',
  OtherIdentityDocument: 'OtherIdentityDocument',
  PhotoCard: 'PhotoCard',
};

/**
 * Names the documents a confidence response put behind its level, for the KYC table to mark.
 *
 * **Absence survives the translation.** An absent list and an empty one are different answers: an
 * empty one would say no document is causing the level, while absence says only that we were not
 * told, which is what an older backend returns. The producer sends the list behind a `Low` level
 * and omits the key otherwise, so absence is the only one of the two that arrives in practice and
 * the pair is held apart here for robustness rather than because the endpoint walks both paths.
 * Both draw no mark, so the difference never reaches the screen, but collapsing it here would
 * destroy it for every later reader of the state.
 */
export function documentsDecreasingConfidence(
  problemDocumentTypes?: TProblemDocumentType[],
): TIdentityDocumentType[] | undefined {
  return problemDocumentTypes?.map((problemDocumentType) => DOCUMENT_NAME[problemDocumentType]);
}
